import {API} from "@/api/generated";
import {
  loudingQuizList,
  Quiz,
  QuizListItem,
  QuestionTypes,
  stateQuiz,
  VersionType
} from "@/page/quiz/typeQuiz";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";
import {defineStore} from "pinia";

type QuizApiResponse =
  import("@/api/openapi").components["schemas"]["QuizResponseDto"];

const isQuizApiResponse = (value: unknown): value is QuizApiResponse => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const requiredKeys = ["_id", "questions"];

  return requiredKeys.every((key) => Object.hasOwn(value, key));
};

const normalizeQuestionType = (type: string): QuestionTypes => {
  switch (type) {
    case "":
    case "select":
    case "radio":
    case "checkbox":
    case "radio-img":
    case "checkbox-img":
    case "text":
    case "textArea":
    case "information":
    case "contacts":
    case "checkbox-dental":
    case "email":
    case "ThankYouPages":
      return type;
    default:
      return "text";
  }
};

export const useQuizStore = defineStore("quizData", {
  state: (): stateQuiz => ({
    loudingQuizList: "success",
    quizList: [],
    quiz: {
      _id: "",
      userId: "",
      ABTestObj: {},
      ABTestBool: false,
      textBellowButtonMobile: "",
      textContactButton: "",
      nowReturn: "",
      name: "",
      logo: "",
      photoRight: [],
      photoRightMobile: "",
      questions: [],
      header: "",
      paragraf: "",
      phone: [],
      address: [],
      widgetInQuiz: false,
      customUrl: "",
      customScript: "",
      сompanyName: "",
      inn: "",
      rangeSize: 0,
      nextButton: "",
      actionList: [],
      actionHeader: "",
      rightSectionList: [],
      mobileVersionQuiz: undefined,
      onlyFormBool: false,
      isActionTopMobile: false,
      showCalcDataInTheEnd: false,
      styleName: {
        type: "onlyName"
      },
      styleContacts: {
        type: "both",
        isDot: false
      },
      isNumericRightSection: {
        pc: false,
        mobile: false
      }
    },
    customUrlValid: true,
    mobileVersion: window.matchMedia("(max-width: 450px)").matches,
    selectedVersion: "Десктоп",
    selectedQuestion: -1,
    startPage: true,
    cachedFiles: [],
    quizStartedValue: ""
  }),

  actions: {
    async fetchQuizList(): Promise<void> {
      if (this.quizList.length > 0) {
        return;
      }
      try {
        this.updateStatusLoadingQuizList("loading");
        this.quizList = await API.getQuizList();
        this.updateStatusLoadingQuizList("success");
      } catch (error) {
        this.updateStatusLoadingQuizList("error");
        throw ["Ошибка при загрузке quizList:", error];
      }
    },

    async fetchQuiz([id, lk]: [string, boolean]): Promise<void> {
      if (id == "") {
        return;
      }
      try {
        let quiz;
        if (lk) {
          quiz = await API.getQuizLk({query: {id}});
        } else {
          quiz = await API.getQuizItem({query: {id}});
        }
        if (!quiz) {
          throw "ошибка получения данных квиза";
        }
        if (
          !isQuizApiResponse(quiz) ||
          !Array.isArray(quiz.questions) ||
          quiz.questions.length === 0
        ) {
          return;
        }
        let ifThankYouPages = quiz.questions.filter((q) => {
          return q?.type == "ThankYouPages";
        });
        if (ifThankYouPages.length == 0) {
          quiz.questions.push({
            question: "благодарность за ответы на все вопросы",
            type: "ThankYouPages",
            placeholder: "",
            contacts: {},
            paragraph: ""
          });
        }

        const normalizedQuiz: Quiz = {
          ...quiz,
          styleContacts: {
            ...quiz.styleContacts,
            type: quiz.styleContacts?.type ?? "both",
            isDot: quiz.styleContacts?.isDot ?? false
          },
          rightSectionList: (Array.isArray(quiz.rightSectionList)
            ? quiz.rightSectionList
            : []
          ).map((section) => ({
            ...section,
            heightMeasurement: section.heightMeasurement === "%" ? "%" : "Px"
          })),
          questions: quiz.questions.map((question) => ({
            ...question,
            type: normalizeQuestionType(question.type),
            paragraph: question.paragraph ?? "",
            contacts: {
              ...question.contacts,
              country:
                question.contacts?.country === "Казахстан (+7)"
                  ? "Казахстан (+7)"
                  : "Россия (+7)",
              headerActionMobile: question.contacts?.headerActionMobile
                ? {
                    ...question.contacts.headerActionMobile,
                    bool: Boolean(question.contacts.headerActionMobile.bool)
                  }
                : undefined
            }
          }))
        };

        this.setQuiz(normalizedQuiz);
      } catch (error) {
        throw "Ошибка при загрузке quiz:" + JSON.stringify(error);
      }
    },

    setQuiz(quiz: Quiz): void {
      let actions = modifyQuiz("get", "", ["actionList"]);
      if (typeof actions == "string") {
        modifyQuiz("set", JSON.parse(actions), ["actionList"]);
      }
      this.quiz = quiz;
    },

    initMobileVersion(): void {
      const mediaQuery = window.matchMedia("(max-width: 450px)");
      const updateMobileVersion = (event: MediaQueryListEvent): void => {
        this.setMobileVersion(event.matches);
      };
      this.setMobileVersion(mediaQuery.matches);
      mediaQuery.addEventListener("change", updateMobileVersion);
    },

    // old mutations

    updateStatusLoadingQuizList(status: loudingQuizList): void {
      this.loudingQuizList = status;
    },

    deleteQuiz(id: string): void {
      this.quizList = this.quizList.filter((q) => {
        return q._id != id;
      });
    },

    addQuiz(quiz: QuizListItem): void {
      this.quizList = [...this.quizList, quiz];
    },

    resetQuiz(): void {
      this.quiz = {
        _id: "",
        userId: "",
        ABTestObj: {},
        ABTestBool: false,
        textBellowButtonMobile: "",
        textContactButton: "",
        nowReturn: "",
        name: "",
        logo: "",
        photoRight: [],
        photoRightMobile: "",
        questions: [],
        header: "",
        paragraf: "",
        phone: [],
        address: [],
        widgetInQuiz: false,
        customUrl: "",
        customScript: "",
        сompanyName: "",
        inn: "",
        rangeSize: 0,
        nextButton: "",
        actionList: [],
        actionHeader: "",
        rightSectionList: [],
        mobileVersionQuiz: undefined,
        onlyFormBool: false,
        isActionTopMobile: false,
        showCalcDataInTheEnd: false,
        styleName: {
          type: "onlyName"
        },
        styleContacts: {
          type: "both",
          isDot: false
        },
        isNumericRightSection: {
          pc: false,
          mobile: false
        }
      };
    },

    setMobileVersion(isMobile: boolean): void {
      this.mobileVersion = isMobile;
    },

    setSelectedVersionLK(isSelected: VersionType): void {
      this.selectedVersion = isSelected;
    },

    setCustomUrlValid(bool: boolean): void {
      this.customUrlValid = bool;
    },

    setStartPage(newVal: boolean): void {
      this.startPage = newVal;
    },

    setSelectedQuestion(newVal: number): void {
      this.selectedQuestion = newVal;
    },

    clearCachedFiles(): void {
      this.cachedFiles = [];
    },

    setQuizStartedValue(quiz: string): void {
      this.quizStartedValue = quiz;
    }
  },

  getters: {
    getStatusLoadingQuizList: (state: stateQuiz): loudingQuizList =>
      state.loudingQuizList,
    getQuizList: (state: stateQuiz): QuizListItem[] => state.quizList,
    getQuiz: (state: stateQuiz): Quiz => state.quiz,
    getMobileVersion: (state: stateQuiz): boolean => state.mobileVersion,
    getSelectedVersion: (state: stateQuiz): VersionType =>
      state.selectedVersion,
    getCustomUrlValid: (state: stateQuiz): boolean => state.customUrlValid,
    getStartPage: (state: stateQuiz): boolean => state.startPage,
    getSelectedQuestion: (state: stateQuiz): number => state.selectedQuestion,
    getCachedFiles: (state: stateQuiz): File[] => state.cachedFiles,
    getQuizStartedValue: (state: stateQuiz): string => state.quizStartedValue
  }
});
